import { Color } from 'pixi.js';
import { MIDisplayMode, type InterfaceLink } from '@/models/ChipModel';
import { TileType } from '@/models/TileTypes';
import type { TileInstance } from '@/models/tiles/TileInstance';
import type { InterfaceTileState } from '@/schemas/tiles';
import { computeMIStateIndex, interpolateColor } from './colorUtils';

/** The exact solid Color-mode sample, shared with ordinary tile rendering. */
export function getMIDisplayColor(link: InterfaceLink, stateIndex: number): string {
  return (
    link.stateColors.get(stateIndex) ||
    interpolateColor('#000000', link.gradientColor ?? link.color, stateIndex / 15)
  );
}

const decode = (value: number) =>
  value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
const encode = (value: number) =>
  value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055;
const channels = (color: string) => {
  const value = new Color(color);
  return [value.red, value.green, value.blue];
};

export interface ColorDisplaySample {
  color: string;
  /** Fixed palette, including colors for states that are currently off. */
  palette: string[];
}

/** Average each channel over its configured contributors, never over unrelated
 * RGB subtiles. Duplicate channels retain brightness; off contributors still dim
 * partial activation. Encode only after averaging linear-light contributions. */
export function combineColorDisplays(samples: ColorDisplaySample[]): number {
  const sums = [0, 0, 0];
  const counts = [0, 0, 0];
  for (const sample of samples) {
    const emitted = channels(sample.color);
    const possible = sample.palette.map(channels);
    for (let channel = 0; channel < 3; channel++) {
      if (possible.some((color) => color[channel] > 0)) counts[channel]++;
      sums[channel] += decode(emitted[channel]);
    }
  }
  const result = sums.map((sum, channel) =>
    Math.round(
      255 * encode(Math.min(1, Math.max(0, counts[channel] ? sum / counts[channel] : 0))),
    ),
  );
  return (result[0] << 16) | (result[1] << 8) | result[2];
}

/** Only reachable four-bit palette entries count; a fully overridden palette
 * must not retain channels from an accent that is never displayed. */
export function configuredDisplayPalette(link: InterfaceLink): string[] {
  const colors = new Set<string>();
  for (let state = 0; state < 16; state++) {
    const override = link.stateColors.get(state);
    if (override) colors.add(override);
    else if (state > 0) colors.add(link.gradientColor ?? link.color);
  }
  return [...colors];
}

/** Read only the placed footprint's own Color displays. Template outputs and
 * image/pixel-art/decorative tiles do not contribute to a solid color. */
export function combinedChipColor(
  tiles: readonly TileInstance[],
  links: Map<string, InterfaceLink>,
): number {
  const samples: ColorDisplaySample[] = [];
  for (const tile of tiles) {
    if (tile.tileType !== TileType.MultiInterface) continue;
    const state = tile.state as InterfaceTileState<unknown>;
    const link = links.get(state?.connectedInterface?.interfaceLinkId);
    if (link?.displayMode !== MIDisplayMode.GRADIENT) continue;
    samples.push({
      color: getMIDisplayColor(link, computeMIStateIndex(tile.meta)),
      palette: configuredDisplayPalette(link),
    });
  }
  return combineColorDisplays(samples);
}
